<template>
  <page>

    <template slot="page-title">2018 General Election Predictions</template>
    <template slot="page-subtitle">
      Last Model Update: <b>{{ sortedDates[sortedDates.length - 1] | readableDate }}</b> | By <a href="https://0ptimus.com" target="_blank">Øptimus</a>
    </template>

    <v-layout row wrap v-if="currentDate">
      <!-- <v-flex xs12>
        <h2 class="section-title mb-2">Control of the House & Senate</h2>
        <div class="section-description mb-4">This chart depicts the control of seats over time. On September 5th we moved from doing weekly updates to daily updates.</div>
      </v-flex> -->

      <v-flex xs12 sm12 class="flex-center" :style="mobile ? 'display: block;' : ''">
        <div class="btn-toggle-wrapper" :style="mobile ? 'margin-bottom: 10px;' : 'margin-bottom: 0px;'">
          <v-btn-toggle v-model="chartChamber">
            <v-btn :color="chartChamber === 'house' ? 'red' : 'grey darken-4'" flat small value="house" class="btn-toggle-option">House</v-btn>
            <v-btn :color="chartChamber === 'senate' ? 'red' : 'grey darken-4'" flat small value="senate" class="btn-toggle-option">Senate</v-btn>
          </v-btn-toggle>
        </div>
        <div class="btn-toggle-wrapper" style="margin: 0 20px;">
          <v-btn-toggle v-model="chartType">
            <v-btn :color="chartType === 'seatMajority' ? 'red' : 'grey darken-4'" flat small value="seatMajority" class="btn-toggle-option">Seat Majority</v-btn>
            <v-btn :color="chartType === 'numOfSeats' ? 'red' : 'grey darken-4'" flat small value="numOfSeats" class="btn-toggle-option">Seat Count</v-btn>
          </v-btn-toggle>
        </div>
      </v-flex>
      <v-flex xs12 sm12 :style="mobile ? 'padding: 0 10px; margin: 10px 0;' : 'padding: 0 20px; margin: 10px 0;'">
        <scoreboard :scoreboardData="scoreboardData" :chartType="chartType" :chamber="chartChamber">
        </scoreboard>
      </v-flex>

      <v-flex xs12 class="mt-2">
        <template v-if="chartChamber === 'house'">
          <time-series v-if="chartType === 'numOfSeats'" @scoreboard-update="scoreboardData = $event" key="houseNumOfSeats" :data="houseModels" chamber="house" :customToggle="true" prop="meanGopSeats" class="mb-5"></time-series>
          <time-series v-if="houseModel && chartType === 'seatMajority'" @scoreboard-update="scoreboardData = $event" key="houseSeatMajority" :data="houseModels" :customToggle="true" chamber="house" prop="probabilityOfGopMajority" class="mb-5"></time-series>
        </template>
        <template v-if="chartChamber === 'senate'">
          <time-series v-if="chartType === 'numOfSeats'" @scoreboard-update="scoreboardData = $event" key="senateNumOfSeats" :data="senateModels" chamber="senate" :customToggle="true" prop="meanGopSeats" class="mb-5"></time-series>
          <time-series v-if="senateModel && chartType === 'seatMajority'" @scoreboard-update="scoreboardData = $event" key="senateSeatMajority" :data="senateModels" chamber="senate" :customToggle="true" prop="probabilityOfGopMajority" class="mb-5"></time-series>
        </template>
      </v-flex>
      <v-flex class="mt-5" xs12 v-if="houseModel">
        <opti-card title="House Model" :date="readableDate(currentDate)">
          <chamber-histogram chamber="house"></chamber-histogram>
          <opti-card :hideTitle="true" v-if="$auth.authenticated">
            <div class="bullet-points" v-html="houseModel.homeBulletPoints"></div>
          </opti-card>
        </opti-card>
      </v-flex>
      <v-flex xs12 v-if="senateModel">
        <opti-card title="Senate Model" :date="readableDate(currentDate)">
          <chamber-histogram chamber="senate"></chamber-histogram>
          <opti-card :hideTitle="true" v-if="$auth.authenticated">
            <div class="bullet-points" v-html="senateModel.homeBulletPoints"></div>
          </opti-card>
        </opti-card>
      </v-flex>
      <v-flex xs12 v-if="!$auth.authenticated">
        <opti-card title="Detailed Model Analysis">
          <login-wrapper :content="true"></login-wrapper>
        </opti-card>
      </v-flex>
    </v-layout>
  </page>
</template>

<script>
import { mapGetters } from 'vuex'
import { ElectionsList, Page } from '@/components'
import cloneDeep from 'lodash.clonedeep'

import { NationwideMap, TimeSeries } from '@/components/charts'
import { ChamberHistogram, LoginWrapper, Scoreboard, TimelineSlider } from '@/components/common'
import Loading from '@/components/common/Loading'
import { OptiCard } from '@/components/optimus'

const name = 'home'

const computed = {
  ...mapGetters({
    models: 'aggregateModels/list',
    dates: 'aggregateModels/dates',
    currentDate: 'aggregateModels/currentDate',
    mobilePhone: 'ux/mobilePhone',
    mobile: 'ux/mobile'
  }),
  houseModels () {
    return this.models.filter(model => model.chamber === 'HOUSE')
  },
  houseModel () {
    return this.houseModels.find(model => model.date === this.currentDate)
  },
  senateModels () {
    return this.models.filter(model => model.chamber === 'SENATE')
  },
  senateModel () {
    return this.senateModels.find(model => model.date === this.currentDate)
  },
  houseMovementBulletPoints () {
    const texts = []
    const index = this.dates.indexOf(this.currentDate)
    const current = this.houseModels.find(model => model.date === this.currentDate)

    if (index > 0) {
      const prev = this.houseModels.find(model => model.date === this.dates[index - 1])
      const diff = Math.round((prev.probabilityOfGopMajority - current.probabilityOfGopMajority) * 100)
      texts.push(
        `Moved ${diff}% since last model`
      )
    }
    return `<ul>${texts.map(text => `<li>${text}</li>`)}</ul>`
  },
  sortedDates () {
    const dates = cloneDeep(this.dates)
    return dates.sort((a, b) => {
      return a.localeCompare(b)
    })
  }
}

const methods = {
  movementBulletPoints (models, key) {
    const texts = []
    const index = this.dates.indexOf(this.currentDate)
    const current = models.find(model => model.date === this.currentDate)
    const prev = models.find(model => model.date === this.dates[index - 1])
    if (prev && index > 0) {
      let diff = Math.round((current[key] - prev[key]) * 10000) / 100
      diff = diff.toString()
      let party = {
        diff: null,
        party: null
      }
      let chamber = models[0].chamber.toLowerCase()
      chamber = chamber.charAt(0).toUpperCase() + chamber.slice(1)
      if (diff.includes('-')) {
        party = 'Democrats'
        diff = diff.replace('-', '')
      } else {
        party = 'Republican'
      }
      if (chamber === 'House') {
        this.houseMovement.diff = diff
        this.houseMovement.party = party.charAt(0)
      } else if (chamber === 'Senate') {
        this.senateMovement.diff = diff
        this.senateMovement.party = party.charAt(0)
      }
      texts.push(
        `Moved ${diff}% towards ${party} controlling the ${chamber} since last model`
      )
    } else {
      this.houseMovement.diff = null
      this.senateMovement.diff = null
    }
    return `<ul>${texts.map(text => `<li>${text}</li>`)}</ul>`
  },
  readableDate (date) {
    if (!date) return ''
    const split = date.split('-')
    const newDate = split[1] + '/' + split[2]
    return newDate
  }
}

const components = {
  ChamberHistogram,
  ElectionsList,
  Loading,
  LoginWrapper,
  OptiCard,
  NationwideMap,
  Page,
  Scoreboard,
  TimelineSlider,
  TimeSeries
}

export default {
  name,
  computed,
  methods,
  components,
  metaInfo: {
    title: 'Home'
  },
  data () {
    return {
      loading: false,
      mapTab: null,
      chartChamber: 'house',
      chartType: 'seatMajority',
      houseMovement: {
        diff: null,
        party: null
      },
      senateMovement: {
        diff: null,
        party: null
      },
      scoreboardData: {},

      pageDescription: 'The midterm elections will be decided on November 6, 2018. Our goal is to accurately forecast what those results will be and show how races change over time. Use this site to monitor predictions as our models are fully updated on Mondays and are updated for polling Tuesday to Friday.'
    }
  },
  mounted () {
    this.movementBulletPoints(this.senateModels, 'probabilityOfGopMajority')
    this.movementBulletPoints(this.houseModels, 'probabilityOfGopMajority')
  },
  watch: {
    houseModels () {
      this.movementBulletPoints(this.senateModels, 'probabilityOfGopMajority')
      this.movementBulletPoints(this.houseModels, 'probabilityOfGopMajority')
    },
    currentDate () {
      this.movementBulletPoints(this.senateModels, 'probabilityOfGopMajority')
      this.movementBulletPoints(this.houseModels, 'probabilityOfGopMajority')
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/Home.vue